import { useMemo, useState } from 'react';
import { Pencil, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ApiError } from '@/lib/query-client';
import { AvatarPersona } from '@/components/rdt/avatar-persona';
import { CampoDecimal } from '@/components/rdt/campo-decimal';
import { esFechaFutura, fFechaCorta, fmtPct, perLabel, periodoActual } from '@/components/rdt/rdt-domain';
import { useEditarChequeo } from '@/hooks/use-rdt-mutations';
import { useMe } from '@/hooks/use-me';
import type { RdtChequeo, RdtDetalle } from '@/types/api';

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message;
  return fallback;
}

/** Resta `delta` meses a un período 'YYYY-MM' — copia local del mismo cálculo
 *  de paso-forms.tsx/rdt-domain.ts (ninguno de los dos lo exporta, cada
 *  consumidor arma su propio rango de opciones): acá arma el tope de atrás
 *  del select de período de un chequeo MIGRADO (R3). */
function periodoMenosMeses(periodo: string, delta: number): string {
  const [y, m] = periodo.split('-').map(Number);
  const total = y * 12 + (m - 1) - delta;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/** Períodos ascendentes desde `hasta`-24 hasta `hasta` inclusive — rango del
 *  select de período editable en un chequeo MIGRADO (R3, Juan 2026-09-14):
 *  24 meses de margen para atrás alcanza y sobra para el desfasaje del
 *  parser de fecha INDEC (~22% de los migrados no coincide con el real, ver
 *  context/design_fix_periodos_migrados.md); el tope de arriba es el mes de
 *  la fecha de índices del chequeo (o el período actual si no tiene) — un
 *  período posterior a los índices que lo originaron no tiene sentido y el
 *  backend lo rechaza con 422. */
function opcionesPeriodoMigrado(hasta: string): string[] {
  const out: string[] = [];
  for (let i = 24; i >= 0; i--) out.push(periodoMenosMeses(hasta, i));
  return out;
}

interface EditFormProps {
  cc: string;
  rdtId: number;
  chequeo: RdtChequeo;
  onClose: () => void;
}

// Mini-form del lápiz de edición (Popover, no inline-en-tabla: la fila es
// angosta) — alcance limitado a valorPct/fechaIndices (resolución #7 del
// panel adversarial): `salta` es SIEMPRE inmutable, se rechaza server-side
// si viene en el body. `periodo` (R3, Juan 2026-09-14) es la excepción:
// editable SOLO en chequeos migrados del histórico (autor null) — en los
// cargados en la app sigue la regla de julio (fijo, este form no le ofrece
// Select). `chequeo.nota` (la nota DEL PASO) ya no se toca desde acá (Juan,
// validación v0.12.0, 2026-09-15: «la nota es del acto, no del dato») — el
// campo «Nota» de más abajo es el motivo del acto de corrección, viaja
// como `motivo` del eco.
function EditarChequeoForm({ cc, rdtId, chequeo, onClose }: EditFormProps) {
  const editarChequeo = useEditarChequeo();
  const esMigrado = chequeo.autor === null;
  // Rango del Select de período (solo migrados): tope de arriba en el mes de
  // la fecha de índices DEL CHEQUEO tal como llegó (no la del campo editable
  // `fechaIndices` de más abajo — así el rango no "salta" de opciones
  // mientras el usuario todavía está tipeando esa fecha), o el período
  // actual si el chequeo no tiene fecha de índices.
  const opcionesPeriodo = useMemo(
    () => (esMigrado ? opcionesPeriodoMigrado(chequeo.fechaIndices?.slice(0, 7) ?? periodoActual()) : []),
    [esMigrado, chequeo.fechaIndices],
  );
  const [periodo, setPeriodo] = useState(chequeo.periodo);
  const [valorPct, setValorPct] = useState<number | null>(chequeo.valorPct);
  const [fechaIndices, setFechaIndices] = useState(chequeo.fechaIndices?.slice(0, 10) ?? '');
  // Nota (Juan, validación v0.12.0, 2026-09-15: «la nota es del acto, no del
  // dato»): un solo campo de texto por acto sobre el chequeo, SIEMPRE vacío
  // — antes coexistían "Nota del chequeo" (precargada, editaba
  // rdt_chequeos.nota) y "Motivo de la corrección" (vacía, viajaba como
  // motivo del eco); ahora hay un único campo que siempre viaja como motivo
  // del eco (rdt_eventos.nota vía `motivo`) y nunca pisa `chequeo.nota` — la
  // nota original del paso (la que se escribió al registrar el chequeo) ya
  // no se edita desde acá, sigue mostrándose tal cual en el ítem del hilo.
  const [nota, setNota] = useState('');
  const periodoCambio = esMigrado && periodo !== chequeo.periodo;
  // Dirty check: período (migrado) / % / fecha de índices — la nota sola
  // NUNCA habilita Guardar (no es un dato del chequeo, es el motivo del acto).
  const hayCambios =
    periodoCambio ||
    valorPct !== chequeo.valorPct ||
    (fechaIndices || null) !== (chequeo.fechaIndices?.slice(0, 10) ?? null);

  function handleGuardar(): void {
    editarChequeo.mutate(
      {
        cc,
        id: rdtId,
        chid: chequeo.id,
        valorPct,
        fechaIndices: fechaIndices || null,
        // R3: `periodo` solo viaja en el body si de verdad cambió — evita un
        // PATCH "sin cambios" en `periodo` que la sección "Sin cambios" del
        // backend igual ignoraría, pero sin sentido mandarlo de más.
        ...(periodoCambio ? { periodo } : {}),
        // Nota (Juan, 2026-09-15): solo viaja si se escribió algo, siempre
        // como `motivo` — nunca se manda `motivo: null`, el backend lo trata
        // como "sin motivo" de todas formas.
        ...(nota.trim() ? { motivo: nota.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.success('Chequeo actualizado');
          onClose();
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo editar el chequeo')),
      },
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="text-[11px] font-semibold text-foreground">Editar chequeo {perLabel(chequeo.periodo)}</div>
      {esMigrado && (
        <div>
          <label className="mb-[3px] block text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground" htmlFor={`ct-periodo-${chequeo.id}`}>
            Período
          </label>
          <Select value={periodo} onValueChange={v => v && setPeriodo(v)}>
            <SelectTrigger id={`ct-periodo-${chequeo.id}`} className="h-8 w-full text-xs">
              <SelectValue>{(v: string) => (v ? perLabel(v) : '—')}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {opcionesPeriodo.map(p => (
                <SelectItem key={p} value={p}>
                  {perLabel(p)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <label className="mb-[3px] block text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground" htmlFor={`ct-pct-${chequeo.id}`}>
          % calculado
        </label>
        <CampoDecimal id={`ct-pct-${chequeo.id}`} adorno="%" valorInicial={chequeo.valorPct} onValueChange={setValorPct} placeholder="—" />
      </div>
      <div>
        <label className="mb-[3px] block text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground" htmlFor={`ct-fi-${chequeo.id}`}>
          Fecha de índices
        </label>
        <Input id={`ct-fi-${chequeo.id}`} type="date" value={fechaIndices} onChange={e => setFechaIndices(e.target.value)} className="h-8 text-xs" />
        {esFechaFutura(fechaIndices) && <p className="mt-1 text-[10.5px] font-medium text-warning">La fecha es futura</p>}
      </div>
      <div>
        <label className="mb-[3px] block text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground" htmlFor={`ct-nota-${chequeo.id}`}>
          Nota
        </label>
        {/* Nota DEL ACTO (Juan, validación v0.12.0, 2026-09-15: «la nota es
            del acto, no del dato»): un único campo, siempre vacío, que queda
            pegado a esta corrección — viaja como `motivo` (rdt_eventos.nota)
            y nunca pisa `chequeo.nota`. La nota original del paso (la que se
            escribió al registrar el chequeo) ya no se edita desde acá; sigue
            mostrándose tal cual en el ítem del chequeo en Actividad. */}
        <Input
          id={`ct-nota-${chequeo.id}`}
          value={nota}
          onChange={e => setNota(e.target.value)}
          className="h-8 text-xs"
          placeholder="Opcional — queda en el hilo con esta corrección"
        />
      </div>
      {/* Aviso (feedback Juan, 2026-08-25 / reescrito para R3, Juan
          2026-09-14): en un chequeo cargado en la app el período sigue fijo
          (PATCH lo rechaza si viene en el body) — la fecha de índices no lo
          mueve. En uno MIGRADO (autor null) es al revés, ahí el período SÍ
          se corrige con el Select de arriba: salió de un parseo de la fecha
          INDEC del legacy sin garantía (~22% de los casos auditados no
          coincide con el real, diagnóstico en
          context/design_fix_periodos_migrados.md) — el salto (`salta`) no se
          toca en ninguno de los dos casos. */}
      {esMigrado ? (
        <p className="text-xs text-text-tertiary">
          El período viene derivado del histórico y puede no coincidir con el real (ver comentarios) — corregilo acá si hace falta; el
          salto no se modifica.
        </p>
      ) : (
        <p className="text-xs text-text-tertiary">El período ({perLabel(chequeo.periodo)}) es fijo — la fecha de índices no lo modifica.</p>
      )}
      <div className="mt-0.5 flex justify-end gap-[7px]">
        <Button type="button" variant="outline" size="sm" onClick={onClose} className="px-[11px] text-xs">
          Cancelar
        </Button>
        <Button type="button" size="sm" onClick={handleGuardar} disabled={editarChequeo.isPending || !hayCambios} className="px-[11px] text-xs">
          {editarChequeo.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
}

interface Props {
  rdt: RdtDetalle;
}

// Tabla compacta de chequeos de índices en la ficha (período · % · salta ·
// fecha de índices · quién) — "todo es por actividad" (resolución cerrada
// 2026-07-11): el ÚNICO camino para registrar un chequeo nuevo es
// «+ Paso → Chequeo de índices»; el botón "Registrar chequeo" que vivía acá
// se sacó (dos caminos confundían). Cada fila con editadoAt lleva la marca
// "(editado)" + un lápiz (gestor+) que corrige valorPct/nota/fechaIndices
// in-place vía un mini-popover — nunca se carga un chequeo nuevo para
// corregir un valor mal tipeado.
export function ChequeosTabla({ rdt }: Props) {
  const { data: me } = useMe();
  const puedeEscribir = me?.rol === 'gestor' || me?.rol === 'admin';
  const [editingId, setEditingId] = useState<number | null>(null);

  // FIX permisos (review adversarial 2026-07-10): el backend solo permite
  // editar un chequeo a su AUTOR, a un admin, o a cualquier gestor si es
  // histórico (autor null, migradas J2) — mostrarle el lápiz a cualquier
  // gestor generaba un 403 sorpresivo al guardar.
  function puedeEditarPaso(autorId: number | null): boolean {
    if (!me) return false;
    return autorId === null || autorId === me.id || me.rol === 'admin';
  }

  return (
    <div className="px-[18px] pb-4 pt-0.5">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">Chequeos de índices</span>

      {rdt.chequeos.length === 0 ? (
        <p className="mt-2 text-xs text-text-tertiary">Sin chequeos registrados — el primero entra por «+ Paso → Chequeo de índices».</p>
      ) : (
        <div className="mt-2.5 overflow-x-auto">
          <Table aria-label="Chequeos de índices" className="w-full border-collapse text-xs">
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className="h-auto border-b border-border-subtle py-[5px] pr-2 pl-0 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-tertiary">
                  Período
                </TableHead>
                <TableHead scope="col" className="h-auto border-b border-border-subtle py-[5px] pr-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-tertiary">
                  %
                </TableHead>
                <TableHead scope="col" className="h-auto border-b border-border-subtle py-[5px] pr-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-tertiary">
                  ¿Salta?
                </TableHead>
                <TableHead scope="col" className="h-auto border-b border-border-subtle py-[5px] pr-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-tertiary">
                  F. índices
                </TableHead>
                <TableHead scope="col" className="h-auto border-b border-border-subtle py-[5px] pr-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-tertiary">
                  Quién
                </TableHead>
                {puedeEscribir && (
                  <TableHead scope="col" className="h-auto border-b border-border-subtle py-[5px] pr-0 pl-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-tertiary">
                    <span className="sr-only">Editar</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rdt.chequeos.map(c => (
                <TableRow key={c.id}>
                  <TableCell className="border-b border-border-subtle py-1.5 pr-2 pl-0 tabular-nums whitespace-nowrap text-muted-foreground last:border-b-0">
                    {perLabel(c.periodo)}
                    {c.editadoAt && <span className="ml-1 text-[9.5px] italic text-text-tertiary">(editado)</span>}
                  </TableCell>
                  <TableCell className="border-b border-border-subtle py-1.5 pr-2 tabular-nums whitespace-nowrap text-muted-foreground last:border-b-0">
                    {c.valorPct != null ? `${fmtPct.format(c.valorPct)} %` : '—'}
                  </TableCell>
                  <TableCell className="border-b border-border-subtle py-1.5 pr-2 tabular-nums whitespace-nowrap last:border-b-0">
                    {c.salta ? (
                      <span className="inline-flex items-center gap-1 font-[650] text-salta">
                        SALTA <Zap className="h-3 w-3" />
                      </span>
                    ) : (
                      <span className="text-muted-foreground">No</span>
                    )}
                  </TableCell>
                  <TableCell className="border-b border-border-subtle py-1.5 pr-2 tabular-nums whitespace-nowrap text-muted-foreground last:border-b-0">
                    {fFechaCorta(c.fechaIndices) ?? '—'}
                  </TableCell>
                  <TableCell className="border-b border-border-subtle py-1.5 pr-2 whitespace-nowrap text-muted-foreground last:border-b-0">
                    {c.autor ? <AvatarPersona nombre={c.autor.nombre} fotoUrl={c.autor.fotoUrl} size={20} /> : '—'}
                  </TableCell>
                  {puedeEscribir && (
                    <TableCell className="border-b border-border-subtle py-1.5 pr-0 pl-2 last:border-b-0">
                      {/* Archivada (solo lectura) o sin permiso sobre ESTE
                          chequeo puntual (no autor/admin/histórico): celda
                          vacía en vez de un lápiz que terminaría en 403/422 —
                          se mantiene la columna para no correr el resto de
                          filas que sí tienen acción. */}
                      {!rdt.archivada && puedeEditarPaso(c.autor?.id ?? null) && (
                        <Popover open={editingId === c.id} onOpenChange={open => setEditingId(open ? c.id : null)}>
                          <Tooltip>
                            <TooltipTrigger
                              render={
                                <PopoverTrigger
                                  render={<Button type="button" variant="ghost" size="icon-xs" aria-label={`Editar chequeo de ${perLabel(c.periodo)}`} />}
                                />
                              }
                            >
                              <Pencil className="h-3 w-3" />
                            </TooltipTrigger>
                            <TooltipContent>{`Editar chequeo de ${perLabel(c.periodo)}`}</TooltipContent>
                          </Tooltip>
                          <PopoverContent side="left" align="start" className="w-[220px]">
                            <EditarChequeoForm cc={rdt.cc} rdtId={rdt.id} chequeo={c} onClose={() => setEditingId(null)} />
                          </PopoverContent>
                        </Popover>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
